import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import * as d3 from 'd3';
import { feature } from 'topojson-client';
import { languageRegions } from '../../../../../data/languageRegions';
import LanguageIcon from '@mui/icons-material/Language';
import { useTranslation } from 'react-i18next';

// Color palette for languages
const languageColors = {
  'English': '#3b82f6',      // Blue
  'Hindi': '#FF9933',         // Green
  'Spanish': '#f59e0b',       // Amber
  'French': '#8b5cf6',        // Purple
  'German': '#ef4444',        // Red
  'Portuguese': '#06b6d4',    // Cyan
  'Italian': '#ec4899',       // Pink
  'Russian': '#6366f1',       // Indigo
  'Chinese': '#f97316',       // Orange
  'Japanese': '#14b8a6',      // Teal
  'Korean': '#a855f7',        // Violet
  'Arabic': '#22c55e',        // Emerald
  'Dutch': '#eab308',         // Yellow
  'Swedish': '#84cc16',       // Lime
  'Norwegian': '#0ea5e9',     // Sky
  'Danish': '#64748b',        // Slate
  'Finnish': '#06b6d4',       // Cyan
  'Polish': '#dc2626',        // Red-600
  'Czech': '#7c3aed',         // Violet-600
  'Hungarian': '#ea580c',     // Orange-600
  'Romanian': '#059669',      // Emerald-600
  'Bulgarian': '#be185d',     // Pink-600
  'Greek': '#0284c7',         // Sky-600
  'Turkish': '#ca8a04',       // Yellow-600
  'Hebrew': '#0891b2',        // Cyan-600
  'Persian': '#7e22ce',       // Purple-600
  'Urdu': '#16a34a',          // Green-600
  'Bengali': '#0d9488',       // Teal-600
  'Tamil': '#c026d3',         // Fuchsia-600
  'Telugu': '#ea580c',        // Orange-600
  'Marathi': '#65a30d',       // Lime-600
  'Gujarati': '#be123c',      // Rose-600
  'Kannada': '#1e40af',       // Blue-700
  'Malayalam': '#7c2d12',     // Brown
  'Punjabi': '#9f1239',       // Rose-700
  'Thai': '#b91c1c',          // Red-700
  'Vietnamese': '#1e293b',    // Slate-800
  'Indonesian': '#7f1d1d',     // Red-900
  'Malay': '#166534',         // Green-800
  'Tagalog': '#831843',       // Rose-900
  'Swahili': '#78350f',       // Amber-900
  'Amharic': '#422006',       // Yellow-900
  'Yoruba': '#581c87',        // Purple-900
  'Igbo': '#7c2d12',          // Brown
  'Hausa': '#1c1917',         // Stone-900
  'Zulu': '#064e3b',          // Emerald-900
  'Afrikaans': '#78350f',     // Amber-900
  'Xhosa': '#4c1d95',         // Purple-800
};

// Mapping from language codes to full language names
const languageCodeToName = {
  'en': 'English',
  'hi': 'Hindi',
  'ar': 'Arabic',
  'fr': 'French',
  'es': 'Spanish',
  'de': 'German',
  'pt': 'Portuguese',
  'it': 'Italian',
  'ru': 'Russian',
  'zh': 'Chinese',
  'ja': 'Japanese',
  'ko': 'Korean',
  'nl': 'Dutch',
  'sv': 'Swedish',
  'no': 'Norwegian',
  'da': 'Danish',
  'fi': 'Finnish',
  'pl': 'Polish',
  'cs': 'Czech',
  'hu': 'Hungarian',
  'ro': 'Romanian',
  'bg': 'Bulgarian',
  'el': 'Greek',
  'tr': 'Turkish',
  'he': 'Hebrew',
  'fa': 'Persian',
  'ur': 'Urdu',
  'bn': 'Bengali',
  'ta': 'Tamil',
  'te': 'Telugu',
  'mr': 'Marathi',
  'gu': 'Gujarati',
  'kn': 'Kannada',
  'ml': 'Malayalam',
  'pa': 'Punjabi',
  'th': 'Thai',
  'vi': 'Vietnamese',
  'id': 'Indonesian',
  'ms': 'Malay',
  'tl': 'Tagalog',
  'sw': 'Swahili',
  'am': 'Amharic',
  'yo': 'Yoruba',
  'ig': 'Igbo',
  'ha': 'Hausa',
  'zu': 'Zulu',
  'af': 'Afrikaans',
  'xh': 'Xhosa',
};

const getFeatureCountryName = (feature) => {
  const props = feature?.properties || {};
  return (
    props.name ||
    props.NAME ||
    props.NAME_LONG ||
    props.NAME_EN ||
    props.ADMIN ||
    props.name_long ||
    props.admin ||
    ''
  );
};

const decodeJwtPayload = (token) => {
  try {
    const payload = token?.split('.')?.[1];
    if (!payload) return null;

    // Safari is strict about base64url decoding, so normalize and pad first.
    const normalizedPayload = payload
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(payload.length / 4) * 4, '=');

    return JSON.parse(atob(normalizedPayload));
  } catch (error) {
    console.error('Error decoding JWT payload:', error);
    return null;
  }
};

const WORLD_ATLAS_SOURCES = [
  '/countries-110m.json',
  'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json',
  'https://unpkg.com/world-atlas@2/countries-110m.json',
];

const loadWorldAtlas = async () => {
  let lastError = null;

  for (const source of WORLD_ATLAS_SOURCES) {
    try {
      const world = await d3.json(source);
      if (world?.objects?.countries) {
        return world;
      }
      lastError = new Error(`Invalid world atlas payload from ${source}`);
    } catch (error) {
      lastError = error;
    }
  }

  throw lastError || new Error('Unable to load world atlas data');
};

const LanguageWorldMap = () => {
  const { t } = useTranslation();
  const svgRef = useRef(null);
  const showTooltipRef = useRef(() => {});
  const hideTooltipRef = useRef(() => {});
  const [activeLanguages, setActiveLanguages] = useState([]);
  const [mapTooltip, setMapTooltip] = useState(null);

  const showTooltip = useCallback((tooltip) => {
    setMapTooltip(tooltip);
  }, []);

  const hideTooltip = useCallback(() => {
    setMapTooltip(null);
  }, []);

  showTooltipRef.current = showTooltip;
  hideTooltipRef.current = hideTooltip;

  useEffect(() => {
    // Get active languages from token
    const token = localStorage.getItem('token');
    if (token) {
      const payload = decodeJwtPayload(token);
      if (!payload) {
        setActiveLanguages([]);
        return;
      }

      // Get available_languages (language codes) from token
      let languageCodes = payload.available_languages || [];

      // If only one language or no languages provided (B2C/Candidate case), default to all four
      if (languageCodes.length <= 1) {
        languageCodes = ['en', 'hi', 'ar', 'fr'];
      }

      // Map language codes to full language names
      const languageNames = languageCodes
        .map(code => languageCodeToName[code])
        .filter(name => name !== undefined);
      setActiveLanguages(languageNames);
    } else {
      setActiveLanguages([]);
    }
  }, []);

  useEffect(() => {
    const svgElement = svgRef.current;
    if (activeLanguages.length === 0 || !svgElement) return;

    let cancelled = false;
    const svg = d3.select(svgElement);
    svg.selectAll("*").remove();

    const width = 800;
    const height = 450;

    svg
      .attr("viewBox", `0 0 ${width} ${height}`)
      .attr("preserveAspectRatio", "xMidYMid meet")
      .style("display", "block")
      .style("width", "100%")
      .style("max-height", "300px")
      .style("pointer-events", "all");

    const projection = d3.geoNaturalEarth1()
      .scale(120)
      .translate([width / 2, height / 2]);

    const path = d3.geoPath().projection(projection);

    // Helper function to get languages for a country (reverse mapping)
    // This will be defined inside the .then() block after isCountryMatch is defined

    // Get countries that should be highlighted
    // const highlightedCountries = getCountriesForLanguages(activeLanguages);
    // console.log('Active languages:', activeLanguages);
    // console.log('Countries to highlight:', highlightedCountries);

    // Country name mapping for world atlas variations
    const countryNameMap = {
      'United States of America': ['United States', 'USA', 'United States of America', 'U.S.A.', 'US'],
      'United Kingdom': ['United Kingdom', 'UK', 'Great Britain', 'Britain', 'U.K.'],
      'Democratic Republic of the Congo': ['Democratic Republic of the Congo', 'DRC', 'Congo, Democratic Republic of', 'Congo-Kinshasa'],
      'Republic of the Congo': ['Republic of the Congo', 'Congo', 'Congo-Brazzaville'],
      'Ivory Coast': ['Ivory Coast', "Côte d'Ivoire", 'Cote d\'Ivoire'],
      'Myanmar': ['Myanmar', 'Burma'],
      'Czech Republic': ['Czech Republic', 'Czechia'],
      'East Timor': ['East Timor', 'Timor-Leste'],
      'Swaziland': ['Swaziland', 'Eswatini'],
    };

    // Load world data with a local-first fallback chain so Safari/macOS
    // doesn't depend on one external CDN succeeding.
    loadWorldAtlas()
      .then(world => {
        if (cancelled || !svgElement) return;

        const countries = feature(world, world.objects.countries);

        // Helper function to normalize country names for matching
        const normalizeCountryName = (name) => {
          if (!name) return '';
          return name.toLowerCase()
            .replace(/^the\s+/i, '')
            .replace(/[.,]/g, '')
            .replace(/\s+/g, ' ')
            .trim();
        };

        // Helper function to get all possible names for a country (including variations)
        const getAllCountryNames = (country) => {
          const names = [country];
          // Check if country has variations in the map
          for (const [key, variations] of Object.entries(countryNameMap)) {
            if (normalizeCountryName(key) === normalizeCountryName(country)) {
              names.push(...variations);
            }
            // Also check if country is in the variations list
            if (variations.some(v => normalizeCountryName(v) === normalizeCountryName(country))) {
              names.push(key, ...variations);
            }
          }
          return [...new Set(names)]; // Remove duplicates
        };

        // Helper function to check if country matches
        const isCountryMatch = (countryName, targetCountry) => {
          if (!countryName || !targetCountry) return false;

          const normalizedName = normalizeCountryName(countryName);
          const normalizedTarget = normalizeCountryName(targetCountry);

          // Exact match
          if (normalizedName === normalizedTarget) return true;

          // Contains match (both directions) - be more lenient
          if (normalizedName.includes(normalizedTarget) || normalizedTarget.includes(normalizedName)) {
            // Avoid false positives (e.g., "Guinea" matching "Guinea-Bissau")
            const nameWords = normalizedName.split(/\s+/);
            const targetWords = normalizedTarget.split(/\s+/);
            // If one is a single word and the other contains it as a word, it's likely a match
            if (nameWords.length === 1 && targetWords.includes(nameWords[0])) return true;
            if (targetWords.length === 1 && nameWords.includes(targetWords[0])) return true;
            // If both are multi-word and one contains all words of the other, it's a match
            if (nameWords.length > 1 && targetWords.length > 1) {
              const allTargetWordsInName = targetWords.every(word => nameWords.some(nw => nw.includes(word) || word.includes(nw)));
              const allNameWordsInTarget = nameWords.every(word => targetWords.some(tw => tw.includes(word) || word.includes(tw)));
              if (allTargetWordsInName || allNameWordsInTarget) return true;
            }
          }

          // Check against all variations
          const allTargetNames = getAllCountryNames(targetCountry);
          return allTargetNames.some(name => {
            const normalized = normalizeCountryName(name);
            return normalized === normalizedName ||
              normalized.includes(normalizedName) ||
              normalizedName.includes(normalized);
          });
        };

        // Helper function to get languages for a country (reverse mapping)
        const getLanguagesForCountry = (countryName) => {
          const languages = [];
          activeLanguages.forEach(language => {
            const regions = languageRegions[language];
            if (regions) {
              // Check if country matches any region for this language using the same matching logic
              const matches = regions.some(region => {
                return isCountryMatch(countryName, region);
              });
              if (matches) {
                languages.push(language);
              }
            }
          });
          return languages;
        };

        // Helper function to get color for a country based on its languages
        const getCountryColor = (countryName) => {
          const languages = getLanguagesForCountry(countryName);
          if (languages.length === 0) return "#e5e7eb"; // Gray for unsupported

          // Use the first language's color
          const firstLanguage = languages[0];
          return languageColors[firstLanguage] || "#3b82f6"; // Default blue if color not found
        };

        // Get all property names from first country for debugging
        if (countries.features.length > 0) {
          // const firstCountry = countries.features[0];
          // console.log('Sample country properties:', Object.keys(firstCountry.properties));
          // console.log('Sample country data:', firstCountry.properties);
          // Log a few country names to see the format
          countries.features.slice(0, 10).forEach(f => {
            // console.log('Country:', f.properties.NAME || f.properties.NAME_LONG || f.properties.ADMIN || 'Unknown',
            //   'Properties:', Object.keys(f.properties));
          });
        }

        // Count how many countries we're trying to match
        // console.log(`Total countries in map: ${countries.features.length}`);
        // console.log(`Countries to highlight: ${highlightedCountries.length}`);

        let matchedCount = 0;

        svg.selectAll("path")
          .data(countries.features)
          .enter()
          .append("path")
          .attr("d", path)
          .attr("fill", d => {
            const countryName = getFeatureCountryName(d);
            const languages = getLanguagesForCountry(countryName);
            const isHighlighted = languages.length > 0;

            if (isHighlighted) {
              matchedCount++;
              // console.log(`[${matchedCount}] Highlighting: "${countryName}" with languages: ${languages.join(', ')}`);
            }

            return getCountryColor(countryName);
          })
          .attr("stroke", "#ffffff")
          .attr("stroke-width", 0.5)
          .style("cursor", "pointer")
          .style("pointer-events", "all")
          .on("mouseover", function (event, d) {
            const countryName = getFeatureCountryName(d);
            const languages = getLanguagesForCountry(countryName);
            const isHighlighted = languages.length > 0;
            const currentColor = getCountryColor(countryName);

            const hoverColor = d3.rgb(currentColor).darker(0.3).toString();
            d3.select(this).attr("fill", isHighlighted ? hoverColor : "#d1d5db");

            if (countryName && isHighlighted) {
              showTooltipRef.current({
                x: event.pageX,
                y: event.pageY,
                country: countryName,
                languages,
                color: currentColor,
              });
            } else {
              hideTooltipRef.current();
            }
          })
          .on("mousemove", function (event, d) {
            const countryName = getFeatureCountryName(d);
            const languages = getLanguagesForCountry(countryName);
            const isHighlighted = languages.length > 0;
            if (countryName && isHighlighted) {
              showTooltipRef.current({
                x: event.pageX,
                y: event.pageY,
                country: countryName,
                languages,
                color: getCountryColor(countryName),
              });
            }
          })
          .on("mouseout", function (event, d) {
            const countryName = getFeatureCountryName(d);
            d3.select(this).attr("fill", getCountryColor(countryName));
            hideTooltipRef.current();
          });

        // Log summary after all countries are processed
        setTimeout(() => {
          // console.log(`Total countries matched and highlighted: ${matchedCount}`);
          if (matchedCount === 0) {
            console.warn('WARNING: No countries were matched! This might indicate a property name mismatch.');
            console.log('First 20 country names from map:',
              countries.features.slice(0, 20).map(f =>
                f.properties.NAME || f.properties.NAME_LONG || f.properties.ADMIN || 'Unknown'
              ));
          }
        }, 100);
      })
      .catch(error => {
        console.error("Error loading world map data:", error);
      });

    return () => {
      cancelled = true;
      hideTooltipRef.current();
      d3.select(svgElement).selectAll('*').remove();
    };
  }, [activeLanguages]);

  return (
    <div className="flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.04)]">
      <div className="h-[0.05rem] w-full shrink-0 bg-gradient-to-r from-sky-400 to-fuchsia-500" />
      <div className="flex items-start gap-3 p-4 sm:p-5">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-sky-50 to-fuchsia-50 text-sky-600 ring-1 ring-sky-100">
          <LanguageIcon sx={{ fontSize: 20 }} />
        </div>

        <div className="flex min-w-0 flex-col">
          <h3 className="text-base font-semibold text-slate-900" style={{ fontFamily: "'Poppins', sans-serif" }}>
            {t('dashboard.languageCoverage')}
          </h3>
          <p className="mt-1 text-xs font-medium text-slate-500" style={{ fontFamily: "'Poppins', sans-serif" }}>
            {t('dashboard.languageCoverageDescription')}
          </p>
        </div>
      </div>

      <div className="relative flex min-h-0 flex-1 flex-col items-center px-4 pb-4 sm:px-5 sm:pb-5">
        <svg ref={svgRef}></svg>

        {mapTooltip &&
          createPortal(
            <div
              role="tooltip"
              className="pointer-events-none"
              style={{
              position: 'absolute',
              left: mapTooltip.x + 12,
              top: mapTooltip.y + 12,
              zIndex: 99999,
              background: mapTooltip.color,
              color: '#fff',
              padding: '8px 12px',
              borderRadius: '8px',
              fontSize: '12px',
              fontFamily: "'Poppins', sans-serif",
              boxShadow: '0 4px 14px rgba(0, 0, 0, 0.2)',
              maxWidth: '260px',
              lineHeight: 1.4,
              border: '2px solid rgba(255, 255, 255, 0.35)',
            }}
          >
            <div style={{ fontWeight: 600 }}>{mapTooltip.country}</div>
            <div style={{ marginTop: '4px', opacity: 0.95 }}>
              {mapTooltip.languages.join(', ')}
            </div>
          </div>,
          document.body
        )}

        <ul
          className="mt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-y-2 gap-x-4 text-sm"
          style={{ fontFamily: "'Poppins', sans-serif" }}
        >
          {activeLanguages.map((language) => {
            const color = languageColors[language] || "#0ea5e9";
            return (
              <li key={language} className="flex items-center gap-2">
                <span
                  className="w-3 h-3 rounded-full shrink-0"
                  style={{ backgroundColor: color }}
                />
                <span
                  className="truncate font-medium"
                  style={{ color: '#525252' }}
                >
                  {language}
                </span>
              </li>
            );
          })}

          <li className="flex items-center gap-2">
            <span
              className="w-3 h-3 rounded-full shrink-0"
              style={{ backgroundColor: '#d4d4d4' }}
            />
            <span
              className="font-medium"
              style={{ color: '#525252' }}
            >
              {t('dashboard.otherRegions')}
            </span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default LanguageWorldMap;
